import { useCallback, useEffect, useRef, useState } from "react";

export const AUTO_SAVE_ACTIVE_INTERVAL_MS = 15000;
export const AUTO_SAVE_IDLE_INTERVAL_MS = 30000;

const EMPTY_SNAPSHOT = "";
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;
const HASH_RADIX = 16;

const getDefaultSnapshot = () => EMPTY_SNAPSHOT;
const saveNothing = async () => false;

/**
 * @param {string | null | undefined} snapshot
 * @returns {string}
 */
export const createContentHash = (snapshot = EMPTY_SNAPSHOT) => {
  const contentSnapshot = String(snapshot ?? EMPTY_SNAPSHOT);
  let hash = FNV_OFFSET_BASIS;

  for (let index = 0; index < contentSnapshot.length; index += 1) {
    hash ^= contentSnapshot.charCodeAt(index);
    hash = Math.imul(hash, FNV_PRIME);
  }

  return `${contentSnapshot.length}:${(hash >>> 0).toString(HASH_RADIX)}`;
};

const useLatestRef = (value) => {
  const latestRef = useRef(value);

  useEffect(() => {
    latestRef.current = value;
  }, [value]);

  return latestRef;
};

const readSnapshotChange = ({ getSnapshotRef, lastSavedHashRef }) => {
  const latestSnapshot = getSnapshotRef.current();
  const latestHash = createContentHash(latestSnapshot);

  return {
    hasChanged: latestHash !== lastSavedHashRef.current,
    latestHash,
  };
};

const saveChangedSnapshot = async ({
  lastSavedHashRef,
  latestHash,
  onSaveRef,
}) => {
  const didSave = await onSaveRef.current();
  if (!didSave) return false;

  lastSavedHashRef.current = latestHash;
  return true;
};

const getNextCheckInterval = (didSave) =>
  didSave ? AUTO_SAVE_ACTIVE_INTERVAL_MS : AUTO_SAVE_IDLE_INTERVAL_MS;

const completeAutoSaveCheck = ({
  delayMs,
  isCancelledRef,
  scheduleCheck,
  setCheckIntervalMs,
}) => {
  if (isCancelledRef.current) return;

  setCheckIntervalMs(delayMs);
  scheduleCheck(delayMs);
};

const startAutoSaveSaving = ({ isSavingRef, setIsSaving }) => {
  isSavingRef.current = true;
  setIsSaving(true);
};

const stopAutoSaveSaving = ({
  isCancelledRef,
  isSavingRef,
  setIsSaving,
}) => {
  isSavingRef.current = false;
  if (isCancelledRef.current) return;

  setIsSaving(false);
};

const completeUnchangedCheck = (options) => {
  completeAutoSaveCheck({
    ...options,
    delayMs: AUTO_SAVE_IDLE_INTERVAL_MS,
  });
};

const completeChangedCheck = ({ didSave, ...options }) => {
  completeAutoSaveCheck({
    ...options,
    delayMs: getNextCheckInterval(didSave),
  });
};

const runAutoSaveCheck = async (options) => {
  const { isCancelledRef, isSavingRef, lastSavedHashRef, onSaveRef } = options;
  if (isSavingRef.current) return;

  const snapshotChange = readSnapshotChange(options);
  if (!snapshotChange.hasChanged) {
    completeUnchangedCheck(options);
    return;
  }

  startAutoSaveSaving(options);
  const didSave = await saveChangedSnapshot({
    lastSavedHashRef,
    latestHash: snapshotChange.latestHash,
    onSaveRef,
  });
  stopAutoSaveSaving(options);
  if (isCancelledRef.current) return;

  completeChangedCheck({ ...options, didSave });
};

const pauseAutoSaveLoop = ({
  clearAutoSaveTimer,
  setCheckIntervalMs,
  setIsSaving,
}) => {
  clearAutoSaveTimer();
  setCheckIntervalMs(AUTO_SAVE_ACTIVE_INTERVAL_MS);
  setIsSaving(false);
};

const startAutoSaveLoop = (options) => {
  const { clearAutoSaveTimer, timerIdRef } = options;
  const isCancelledRef = { current: false };
  const scheduleCheck = (delayMs) => {
    timerIdRef.current = setTimeout(runCheck, delayMs);
  };
  const runCheck = () =>
    runAutoSaveCheck({
      ...options,
      isCancelledRef,
      scheduleCheck,
    });

  scheduleCheck(AUTO_SAVE_ACTIVE_INTERVAL_MS);
  return () => {
    isCancelledRef.current = true;
    clearAutoSaveTimer();
  };
};

const useAutoSaveTimerState = () => {
  const timerIdRef = useRef(null);
  const isSavingRef = useRef(false);
  const [checkIntervalMs, setCheckIntervalMs] = useState(
    AUTO_SAVE_ACTIVE_INTERVAL_MS
  );
  const [isSaving, setIsSaving] = useState(false);

  const clearAutoSaveTimer = useCallback(() => {
    if (!timerIdRef.current) return;

    clearTimeout(timerIdRef.current);
    timerIdRef.current = null;
  }, []);

  return {
    checkIntervalMs,
    clearAutoSaveTimer,
    isSaving,
    isSavingRef,
    setCheckIntervalMs,
    setIsSaving,
    timerIdRef,
  };
};

const getAutoSaveLoopCleanup = ({
  canSave,
  clearAutoSaveTimer,
  getSnapshotRef,
  isEnabled,
  isSavingRef,
  lastSavedHashRef,
  onSaveRef,
  setCheckIntervalMs,
  setIsSaving,
  timerIdRef,
}) => {
  if (!isEnabled || !canSave) {
    pauseAutoSaveLoop({ clearAutoSaveTimer, setCheckIntervalMs, setIsSaving });
    return undefined;
  }

  return startAutoSaveLoop({
    clearAutoSaveTimer,
    getSnapshotRef,
    isSavingRef,
    lastSavedHashRef,
    onSaveRef,
    setCheckIntervalMs,
    setIsSaving,
    timerIdRef,
  });
};

const useAutoSaveTimerEffect = ({
  canSave,
  getSnapshotRef,
  isEnabled,
  lastSavedHashRef,
  onSaveRef,
  timerState,
}) => {
  const { clearAutoSaveTimer, isSavingRef, setCheckIntervalMs } = timerState;
  const { setIsSaving, timerIdRef } = timerState;

  useEffect(
    () =>
      getAutoSaveLoopCleanup({
        canSave,
        clearAutoSaveTimer,
        getSnapshotRef,
        isEnabled,
        isSavingRef,
        lastSavedHashRef,
        onSaveRef,
        setCheckIntervalMs,
        setIsSaving,
        timerIdRef,
      }),
    [canSave, clearAutoSaveTimer, getSnapshotRef, isEnabled, isSavingRef, lastSavedHashRef, onSaveRef, setCheckIntervalMs, setIsSaving, timerIdRef]
  );
};

const useAutoSaveTimer = ({
  canSave,
  getSnapshotRef,
  isEnabled,
  lastSavedHashRef,
  onSaveRef,
}) => {
  const timerState = useAutoSaveTimerState();
  useAutoSaveTimerEffect({
    canSave,
    getSnapshotRef,
    isEnabled,
    lastSavedHashRef,
    onSaveRef,
    timerState,
  });

  return {
    checkIntervalMs: timerState.checkIntervalMs,
    isSaving: timerState.isSaving,
  };
};

/**
 * @param {{
 *   canSave?: boolean,
 *   getSnapshot?: () => string,
 *   isEnabled?: boolean,
 *   onSave?: () => Promise<boolean>
 * }} options
 */
export const useAutoSave = ({
  canSave = true,
  getSnapshot = getDefaultSnapshot,
  isEnabled = false,
  onSave = saveNothing,
} = {}) => {
  const getSnapshotRef = useLatestRef(getSnapshot);
  const onSaveRef = useLatestRef(onSave);
  const lastSavedHashRef = useRef(createContentHash(getSnapshot()));
  const autoSaveTimer = useAutoSaveTimer({
    canSave,
    getSnapshotRef,
    isEnabled,
    lastSavedHashRef,
    onSaveRef,
  });

  const markSaved = useCallback(
    (snapshot = getSnapshotRef.current()) => {
      lastSavedHashRef.current = createContentHash(snapshot);
    },
    [getSnapshotRef]
  );

  return {
    ...autoSaveTimer,
    markSaved,
  };
};
